import React, { type FC, InputHTMLAttributes } from 'react'

import { useController, useFormContext } from 'react-hook-form'

import { classNames } from '@/utils/helpers'

import { TextField } from '../text-field.tsx'

interface FormFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string | React.ReactNode
  name: string
  showErrors?: boolean
  isPassword?: boolean
  /** @deprecated use prefix */
  prefixIcon?: React.ReactNode
  prefix?: React.ReactNode
  suffix?: React.ReactNode
  withEmojiPicker?: boolean
  withLengthIndicator?: boolean
  testId?: string
  children?: React.ReactNode
  wrapperClassName?: string
  formatValue?: (value: string) => string
}

export const FormField: FC<FormFieldProps> = ({
  label,
  placeholder,
  name,
  showErrors = true,
  isPassword,
  type = 'text',
  className,
  disabled,
  prefixIcon,
  prefix,
  suffix,
  withEmojiPicker,
  withLengthIndicator,
  wrapperClassName,
  formatValue,
  testId,
  children,
}) => {
  const { control } = useFormContext()
  const { field, fieldState } = useController({ name, control })

  return (
    <div className={wrapperClassName}>
      <div className={classNames('flex w-full flex-col gap-1.5', className)}>
        {label && (
          <label
            htmlFor={field.name}
            className="text-text-default text-sm font-medium"
          >
            {label}
          </label>
        )}
        <div className="relative flex flex-grow items-center">
          {children || (
            <TextField
              {...field}
              testId={testId}
              ref={field.ref}
              placeholder={placeholder}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={(event) => {
                const value = formatValue
                  ? formatValue(event.target.value)
                  : event.target.value

                if (type === 'number') {
                  const v = Number(value)

                  return field.onChange(isNaN(v) ? 0 : v)
                }

                field.onChange(value)
              }}
              // @ts-ignore
              // TODO add better types and refactor Field component when all fields variation will be presented
              Icon={
                prefixIcon && (
                  <span className="flex items-center">{prefixIcon}</span>
                )
              }
              prefix={prefix}
              suffix={suffix}
              type={type === 'number' ? 'text' : type}
              isPassword={isPassword}
              labelClassName="w-full"
              disabled={disabled}
              withEmojiPicker={withEmojiPicker}
              className={classNames(
                'h-10 w-full flex-grow',
                showErrors && fieldState.error && 'border-danger-default',
              )}
            />
          )}
        </div>
        {withLengthIndicator && (
          <p className="text-text-sub text-end text-xs">
            {field.value.length} / 100
          </p>
        )}
      </div>
      {showErrors && fieldState.error && (
        <p
          className="text-danger-default mt-1 text-xs"
          data-testid={`${testId}-error`}
        >
          {fieldState.error.message}
        </p>
      )}
    </div>
  )
}
